import React, { useState } from 'react';
import { useAuth } from './SimpleAuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import {
  Mail, Lock, User, AlertCircle, ArrowRight, ChevronRight,
  Activity, Calculator, ShieldAlert, LineChart, Database, Workflow, FileSpreadsheet,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Brand wordmark (mirrors Login.js)
 * ------------------------------------------------------------------ */
function Brand({ size = 'md' }) {
  const t = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-base' : 'text-xl';
  return (
    <div className={`flex items-center gap-2.5 font-display font-extrabold ${t} tracking-tight`}>
      <div className="relative inline-flex h-9 w-9 items-center justify-center border border-current bg-transparent">
        <div className="absolute inset-1 border border-current/40" />
        <span className="relative font-mono text-[11px] font-bold text-emerald-glow">B.AI</span>
      </div>
      <span className="leading-none">
        BuildMetri<span className="text-emerald-glow">.AI</span>
      </span>
    </div>
  );
}

const TRIAL_BENEFITS = [
  { icon: Calculator,       label: '3 full project calculations',  sub: 'Every sector · 109 sub-sectors' },
  { icon: Workflow,         label: 'WBS · 8 lifecycle phases',     sub: 'Sub-phases & dependencies' },
  { icon: ShieldAlert,      label: 'Risk &amp; change library',    sub: 'Auto-included by sector' },
  { icon: LineChart,        label: 'EVM · KPI · Visual dashboards', sub: 'Exportable to PDF' },
  { icon: FileSpreadsheet,  label: 'Baseline &amp; Actuals',       sub: 'CSV bulk-import + AI variance' },
  { icon: Database,         label: 'Rate sheets &amp; 30+ FX',     sub: 'Region-calibrated multipliers' },
];

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const trialMode = params.get('trial') === 'true';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) { setError('Please fill in all fields'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    try {
      setError(''); setLoading(true);
      await signup(email, password, name);
      navigate('/');
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') setError('This email is already registered. Please login instead.');
      else if (err.code === 'auth/weak-password')   setError('Password is too weak. Please use a stronger password.');
      else setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-2">
      {/* ---------------------------------------------------------- *
       *  LEFT — value-prop panel (dark)
       * ---------------------------------------------------------- */}
      <div className="relative bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 bg-grid-dense opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full"
             style={{ background: 'radial-gradient(closest-side, hsl(151 100% 45% / 0.18), transparent 70%)' }} />
        <div className="relative flex h-full flex-col justify-between px-8 sm:px-12 lg:px-16 py-10 lg:py-14">
          <Link to="/login" data-testid="signup-brand-link">
            <Brand />
          </Link>

          <div className="max-w-xl mt-12">
            <div className="rise-in inline-flex items-center gap-2 border border-background/30 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-glow">
              <Activity className="h-3 w-3" /> {trialMode ? 'Free trial · 7 days' : 'Create your workspace'}
            </div>
            <h1 className="rise-in mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tighter" style={{ animationDelay: '80ms' }}>
              Get a calibrated estimate<br />
              <span className="text-emerald-glow">before your next standup.</span>
            </h1>
            <p className="rise-in mt-5 text-base sm:text-lg text-background/75 leading-relaxed" style={{ animationDelay: '160ms' }}>
              Skip the 14-tab spreadsheet. BuildMetri.AI ships with the full lifecycle
              estimation stack — calibrated rates, AI variance advisors, and audit-grade outputs.
            </p>

            <div className="rise-in mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-background/15 border border-background/20" style={{ animationDelay: '240ms' }}>
              {TRIAL_BENEFITS.map(({ icon: Icon, label, sub }) => (
                <div key={label} className="bg-foreground p-4 flex items-start gap-3 hover:bg-background/5 transition-colors" data-testid={`benefit-${label.split(' ')[0].toLowerCase()}`}>
                  <Icon className="h-5 w-5 text-emerald-glow mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight text-background" dangerouslySetInnerHTML={{ __html: label }} />
                    <div className="text-[11px] font-mono text-background/60 mt-0.5">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* terminal callout */}
            <div className="rise-in mt-8 border border-background/20 bg-background/5 p-4 font-mono text-[12px] leading-6" style={{ animationDelay: '320ms' }}>
              <div className="flex items-center gap-2 text-background/60">
                <span className="h-2 w-2 bg-emerald-glow inline-block" />
                <span className="uppercase tracking-[0.25em] text-[10px]">Trial includes</span>
              </div>
              <div className="mt-1 text-background/80">
                <span className="text-emerald-glow">3</span> calculations · <span className="text-emerald-glow">12</span> modules ·
                <span className="text-emerald-glow"> 30+</span> currencies · <span className="text-emerald-glow">PDF</span> export<span className="caret"></span>
              </div>
            </div>
          </div>

          <div className="hidden lg:block mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-background/50">
            BuildMetri.AI · Project Lifecycle Cost Estimation Suite · v1.8
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- *
       *  RIGHT — signup form (light, sharp)
       * ---------------------------------------------------------- */}
      <div className="flex items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-md">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ Onboarding</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Create your account</h2>
          <p className="mt-2 text-sm text-muted-foreground">No credit card · cancel any time · {trialMode ? '7-day free trial active' : 'free trial available'}.</p>

          {error && (
            <div className="mt-5 border border-destructive/40 bg-destructive/5 px-3 py-2 flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Full name</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input id="name" type="text" placeholder="Jane Estimator" value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary"
                  required data-testid="signup-name-input" />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Work email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input id="email" type="email" placeholder="you@firm.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary"
                  required data-testid="signup-email-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input id="password" type="password" placeholder="6+ characters" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary"
                    required data-testid="signup-password-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Confirm</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input id="confirmPassword" type="password" placeholder="Repeat" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary"
                    required data-testid="signup-confirm-input" />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-12 mt-2 font-display font-semibold text-sm tracking-wider uppercase shadow-hard"
              style={{ backgroundColor: 'hsl(151 100% 38%)', color: '#000' }}
              data-testid="signup-submit-btn">
              {loading ? 'Creating account…' : (
                <span className="inline-flex items-center gap-2">
                  {trialMode ? 'Start free trial' : 'Create account'} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>

            <div className="flex items-center justify-between pt-2 text-xs">
              <span className="text-muted-foreground">Already have an account?</span>
              <Link to="/login" className="inline-flex items-center gap-1 font-semibold hover:text-emerald-glow" data-testid="signup-login-link">
                Sign in <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </form>

          <p className="mt-8 text-[11px] font-mono text-muted-foreground leading-relaxed">
            By creating an account you agree to BuildMetri.AI's <span className="text-foreground">Terms</span> and <span className="text-foreground">Privacy Policy</span>.
            Premium upgrade available at <span className="text-emerald-glow">$45 / quarter</span>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
